import { Suspense, useCallback, useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { DateTime } from "luxon"
import { MRT_ColumnDef } from "material-react-table"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    getEditMessageRoute,
    MERCHANT_ADMIN_ROUTE,
    MESSAGES_ROUTE_CREATE_MESSAGE_ITEM,
} from "../../../../../constants/routes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { FetchMessagesResponse, MerchantMessagesQuery, deleteMessageItem, fetchMessages } from "../messagesApi"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { Message } from "../messagesTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { LockedDialog } from "../components/LockedDialog"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { makeStyles } from "tss-react/mui"
import { DestructiveMenuItem } from "../../../../../shared/components/menus/DestructiveMenuItem"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}

const useStyles = makeStyles()((theme) => ({
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
}))

const selectMessages = (data: FetchMessagesResponse) => data?.messages || []

function MessagesTable() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { classes } = useStyles()

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const [showLockedDialog, setShowLockedDialog] = useState(false)

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)

    const devicesResult = useApiQuery(fetchMessages, {
        queryName: MerchantMessagesQuery.LIST,
        dependencies: {
            merchantId,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: selectMessages,
    })
    const { ApiQueryTable, columnFactory } = useApiQueryTable(devicesResult)

    const columns = useMemo<MRT_ColumnDef<Message>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("messages-table-subject-column"),
                accessorKey: "title",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("messages-table-publish-time-column"),
                    accessorKey: "publishTime",
                },
                (row) => row.publishTime,
            ),
        ],
        [columnFactory, l10n],
    )

    const { mutate: callDeleteMessageItem } = useApiMutation(deleteMessageItem, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantMessagesQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message:
                    data?.serviceResponse?.displayMessage ?? l10n.getString("messages-table-delete-success-message"),
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteMessageItem({ messageId: confirmModalState.itemId })
        } else {
            throw new Error("Message id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteMessageItem])

    return (
        <>
            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            onClick={() => {
                                if (row.original?.editDetails?.update?.canUpdate) {
                                    navigate(getEditMessageRoute(row.original.id))
                                } else {
                                    setShowLockedDialog(true)
                                }
                                closeMenu()
                            }}
                            key={`update-${row.original.id}`}
                        >
                            <LocalizedStrict id="messages-table-edit-button">
                                <>Edit</>
                            </LocalizedStrict>
                        </MenuItem>,
                        <DestructiveMenuItem
                            onClick={() => {
                                if (row.original?.editDetails?.delete?.canDelete) {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.title}`,
                                        itemId: row.original.id,
                                    })
                                } else {
                                    setShowLockedDialog(true)
                                }
                                closeMenu()
                            }}
                            className={classes.deleteItem}
                            key={`delete-${row.original.id}`}
                        >
                            <LocalizedStrict id="messages-table-delete-button">
                                <>Delete</>
                            </LocalizedStrict>
                        </DestructiveMenuItem>,
                    ]}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        </StandardTableFilter>
                    )}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    open={confirmModalState.showConfirmModal}
                    typeOfEntity={l10n.getString("messages-table-delete-modal-title")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    onClose={() => setConfirmModalState(defaultModalState)}
                />
            )}
            {showLockedDialog && <LockedDialog open={showLockedDialog} onClose={() => setShowLockedDialog(false)} />}
        </>
    )
}

export function Messages() {
    const navigate = useNavigate()

    return (
        <>
            <LocalizedStrict id="messages-list-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MESSAGES}
                    title="Push messages"
                    rightElement={
                        <LocalizedStrict id="messages-list-create-button">
                            <StandardButton onClick={() => navigate(MESSAGES_ROUTE_CREATE_MESSAGE_ITEM)}>
                                Create message
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-communication-header">
                            <Breadcrumb>Communication</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-messages">
                            <Breadcrumb>Push messages</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <MessagesTable />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
